import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import classNames from 'classnames';

import './BottomMenu.scss';
import { select, RootState, Dispatch } from '../../configs/store';
import BottomMenuContext from './Context/BottomMenu.context';
import Icon from '../Icon/Icon';
import BottomMenuDrawer from './BottomMenuDrawer/BottomMenuDrawer';

function BottomMenu() {
  const [tab, setTab] = useState('');
  const [myBetsCount, setMyBetsCount] = useState(0);

  const { bets } = useSelector((state: RootState) => state.betslip);
  const { searchResults } = useSelector((state: RootState) => state.events);
  const { filterResults } = useSelector((state: RootState) => state.filters);
  const { drawer } = useSelector((state: RootState) => state.content);
  const { customer } = useSelector((state: RootState) => state.auth);
  const total = useSelector(select.filters.total);
  const totalTopResults = useSelector(select.filters.totalTopResults);
  const dispatch = useDispatch<Dispatch>();

  const { t } = useTranslation(['common']);
  const location = useLocation();

  const context = {
    selectedTab: tab,
    handleOpen: (type: string) => {
      setTab(tab === type ? '' : type);
    },
    myBetsCount
  };

  useEffect(() => {
    async function fetchBetHistory() {
      const { results } = await dispatch.account.getBetHistory({ page: -1, fromDate: undefined, type: 'openBets', betType:'allTypes' });
      setMyBetsCount(results.collection?.length || 0);
    }

    fetchBetHistory();
  }, [customer]);

  useEffect(() => {
    if (tab && tab !== '') {
      document.body.classList.add('disable-scroll');
    } else {
      document.body.classList.remove('disable-scroll');
    }

    return () => {
      document.body.classList.remove('disable-scroll');
    };
  }, [tab]);

  useEffect(() => {
    if (drawer !== '') {
      setTab(drawer);
      dispatch.content.openDrawer('');
    }
  }, [drawer]);

  const setSearch = () => {
    if (location.pathname.includes('/racing')) {
      return t('bottomMenu.races');
    } else if (location.pathname.includes('/lotto')) {
      return t('bottomMenu.draws');
    } else {
      return t('bottomMenu.sports');
    }
  };

  const setExtra = () => {
    if (location.pathname.includes('/racing')) {
      return t('bottomMenu.meetings');
    } else if (location.pathname.includes('/lotto')) {
      return t('bottomMenu.lobby');
    } else {
      return t('bottomMenu.inPlay');
    }
  };

  const setExtraIcon = () => {
    if (location.pathname.includes('/racing')) {
      return <Icon icon="meetings" className="bottom-navigation__icon-extra" />;
    } else if (location.pathname.includes('/lotto')) {
      return <Icon icon="numberIcon" className="bottom-navigation__icon-extra" />;
    } else {
      return <Icon icon="chronometer" className="bottom-navigation__icon-extra" />;
    }
  };

  const setExtraLink = () => {
    if (location.pathname.includes('/racing')) {
      return '/racing/today';
    } else if (location.pathname.includes('/lotto')) {
      return '/lotto';
    } else {
      return '/sports-betting/live';
    }
  };

  return (
    <BottomMenuContext.Provider value={ context }>
      <BottomMenuDrawer content="betslip" open={ tab === 'betslip' } variant="persistent" />
      <BottomMenuDrawer content="filter" open={ tab === 'filter' } variant="persistent" />
      <BottomMenuDrawer content="myBets" open={ tab === 'myBets' } variant="persistent" />

      <div className="bottom-navigation">
        <NavLink
          to={ `/sports-betting/prematch${ location.search }` }
          className="bottom-navigation__container"
          activeClassName="bottom-navigation__container--open"
          onClick={ () => context.handleOpen('') }
          isActive={
            match => {
              if (!match || tab !== '') {
                return false;
              }

              return true;
            }
          }
        >
          <Icon icon="home" className="bottom-navigation__icon-extra" />
          <div className="bottom-navigation__text">
            { t('bottomMenu.home') }
          </div>
        </NavLink>
        <button
          onClick={ () => context.handleOpen('myBets') }
          className={
            classNames({
              'bottom-navigation__container' : true,
              'bottom-navigation__container--open': tab === 'myBets'
            })
          }
        >
          <Icon icon="betslip" className="bottom-navigation__icon-extra" />
          <div className="bottom-navigation__text">
            { t('bottomMenu.myBets') }
          </div>
          {
            myBetsCount > 0 && <span
              className={
                classNames({
                  'bottom-navigation__result' : true,
                  'bottom-navigation__result--active': tab === 'myBets'
                })
              }
            >{ myBetsCount }</span>
          }
        </button>
        <button
          onClick={ () => context.handleOpen('betslip') }
          className={
            classNames({
              'bottom-navigation__container' : true,
              'bottom-navigation__container--open': tab === 'betslip'
            })
          }
        >
          <Icon icon="BetslipMobile" className="bottom-navigation__icon-extra" />
          <div className="bottom-navigation__text">
            { t('bottomMenu.betslip') }
          </div>
          {
            bets.length > 0 && <span
              className={
                classNames({
                  'bottom-navigation__result' : true,
                  'bottom-navigation__result--active': tab === 'betslip'
                })
              }
            >{ bets.length }</span>
          }
        </button>
        <NavLink
          to={ setExtraLink() }
          className="bottom-navigation__container"
          activeClassName="bottom-navigation__container--open"
          onClick={ () => context.handleOpen('') }
          isActive={
            match => {
              if (!match || tab !== '') {
                return false;
              }

              return true;
            }
          }
        >
          { setExtraIcon() }
          <div className="bottom-navigation__text">
            { setExtra() }
          </div>
        </NavLink>
        <button
          onClick={ () => context.handleOpen('filter') }
          className={
            classNames({
              'bottom-navigation__container' : true,
              'bottom-navigation__container--open': tab === 'filter'
            })
          }
        >
          <Icon icon="searchIcon" className="bottom-navigation__icon-extra" />
          <div className="bottom-navigation__text">
            { setSearch() }
          </div>
          {
            (
              location.pathname.includes('/sports-betting') &&
              (location.pathname.includes('/sports-betting') && filterResults.length > 0) &&
              total > 0 || searchResults.totals > 0 || totalTopResults > 0
            ) &&
            <span
              className={
                classNames({
                  'bottom-navigation__result' : true,
                  'bottom-navigation__result--active': tab === 'filter'
                })
              }
            >
              {
                total ||
                searchResults.totals ||
                totalTopResults ||
                filterResults.length
              }
            </span>
          }
        </button>
      </div>
    </BottomMenuContext.Provider>
  );
}

export default BottomMenu;
